import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

/*
 * Charts for the Performance dashboard, drawn as plain SVG / CSS so the bundle
 * gains no chart library and every mark takes the tenant's tokens:
 *
 *  • one series per chart, in the org's brand colour (`--gq-color-1`) —
 *    nominal bars all share it; a value-ramp on product names would only
 *    re-encode the bar length;
 *  • the heatmap is a one-hue ramp of that same colour (sequential);
 *  • text never wears the data colour — labels and values stay in ink tokens;
 *  • hairline, solid, recessive grid; 2px lines; bars ≤ 24px with a rounded
 *    data end and a square baseline;
 *  • every chart answers hover and keyboard focus, and every value it shows is
 *    also reachable as text (direct labels, the readout, or a table view).
 */

/** Width of an element, tracked as it resizes. */
function useWidth<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** A clean axis: 0 to a round maximum in four-ish steps (1 / 2 / 5 × 10ⁿ). */
function niceScale(max: number): { max: number; step: number } {
  if (max <= 0) return { max: 4, step: 1 };
  const rough = max / 4;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  const step = Math.max(1, (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag);
  return { max: Math.ceil(max / step) * step, step };
}

/* ── Trend: area + line with a crosshair ─────────────────────────────── */

export interface TrendDatum {
  /** Short x-axis label, e.g. "23 Sep" or "14:00". */
  tick: string;
  /** Full label for the tooltip, e.g. "Tue, 23 Sep 2026". */
  label: string;
  value: number;
  /** Every figure for this bucket, shown together in the tooltip. */
  rows: readonly { label: string; value: string }[];
}

export function TrendChart({
  data,
  format,
  ariaLabel,
  height = 260,
}: {
  data: readonly TrendDatum[];
  /** Formats axis ticks and the end label (compact). */
  format: (n: number) => string;
  ariaLabel: string;
  height?: number;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);

  const M = { top: 20, right: 20, bottom: 30, left: 46 };
  const w = Math.max(0, width - M.left - M.right);
  const h = height - M.top - M.bottom;
  const n = data.length;
  const { max, step } = niceScale(Math.max(0, ...data.map((d) => d.value)));
  const x = (i: number) => (n <= 1 ? w / 2 : (i / (n - 1)) * w);
  const y = (v: number) => h - (v / max) * h;

  const line = data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d.value).toFixed(1)}`).join("");
  const area = n > 1 ? `${line}L${x(n - 1).toFixed(1)},${h}L0,${h}Z` : "";

  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);

  // As many x labels as fit (~84px apart), always including both ends.
  const slots = Math.min(n, Math.max(2, Math.floor(w / 84)));
  const xLabels =
    n <= 1 ? [0] : [...new Set(Array.from({ length: slots }, (_, k) => Math.round((k * (n - 1)) / (slots - 1))))];

  const pick = (clientX: number, rect: DOMRect) => {
    const px = clientX - rect.left - M.left;
    const i = n <= 1 ? 0 : Math.round((px / w) * (n - 1));
    setActive(Math.max(0, Math.min(n - 1, i)));
  };

  // A hover index outlives the data it was taken on: when a filter shrinks the
  // series (30 days → 10), a remembered index past the end must not be read.
  const shown = active !== null && active < n ? active : null;
  const last = n - 1;

  return (
    <div className="relative" ref={ref}>
      {width > 0 ? (
        <svg
          aria-label={ariaLabel}
          className="block touch-none outline-none focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-brand"
          height={height}
          onBlur={() => setActive(null)}
          onFocus={() => setActive(last)}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") setActive((a) => Math.max(0, (a ?? last) - 1));
            if (e.key === "ArrowRight") setActive((a) => Math.min(last, (a ?? last) + 1));
          }}
          onPointerLeave={() => setActive(null)}
          onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
          role="img"
          tabIndex={0}
          width={width}
        >
          <g transform={`translate(${M.left},${M.top})`}>
            {ticks.map((v) => (
              <g key={v}>
                <line stroke="var(--gq-rule)" strokeWidth={1} x1={0} x2={w} y1={y(v)} y2={y(v)} />
                <text
                  className="fill-muted text-[11px] tabular-nums"
                  dominantBaseline="middle"
                  textAnchor="end"
                  x={-10}
                  y={y(v)}
                >
                  {format(v)}
                </text>
              </g>
            ))}

            {xLabels.map((i) => (
              <text
                className="fill-muted text-[11px]"
                key={i}
                textAnchor={n <= 1 ? "middle" : i === 0 ? "start" : i === last ? "end" : "middle"}
                x={x(i)}
                y={h + 20}
              >
                {data[i]?.tick}
              </text>
            ))}

            {area ? <path d={area} fill="color-mix(in srgb, var(--gq-color-1) 11%, transparent)" /> : null}
            {n > 1 ? (
              <path
                d={line}
                fill="none"
                stroke="var(--gq-color-1)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            ) : null}

            {shown !== null ? (
              <line stroke="var(--gq-ink)" strokeOpacity={0.25} strokeWidth={1} x1={x(shown)} x2={x(shown)} y1={0} y2={h} />
            ) : null}

            {/* The endpoint carries the one direct label; the rest live on the axis and in the tooltip. */}
            {n > 0 ? (
              <>
                <circle
                  cx={x(shown ?? last)}
                  cy={y(data[shown ?? last]!.value)}
                  fill="var(--gq-color-1)"
                  r={4.5}
                  stroke="var(--gq-surface-raised)"
                  strokeWidth={2}
                />
                {shown === null ? (
                  <text
                    className="fill-ink text-[12px] font-semibold tabular-nums"
                    textAnchor={n <= 1 ? "middle" : "end"}
                    x={x(last)}
                    y={y(data[last]!.value) - 10}
                  >
                    {format(data[last]!.value)}
                  </text>
                ) : null}
              </>
            ) : null}
          </g>
        </svg>
      ) : (
        <div style={{ height }} />
      )}

      {shown !== null && data[shown] ? (
        <div
          className="pointer-events-none absolute top-2 z-10 min-w-40 rounded-md border border-rule bg-raised px-3 py-2 shadow-pop"
          role="status"
          style={
            x(shown) + M.left > width / 2
              ? { right: width - (x(shown) + M.left) + 12 }
              : { left: x(shown) + M.left + 12 }
          }
        >
          <p className="text-xs text-muted">{data[shown].label}</p>
          <dl className="mt-1 flex flex-col gap-0.5">
            {data[shown].rows.map((r, i) => (
              <div className="flex items-center justify-between gap-4 text-sm" key={r.label}>
                <dt className="flex items-center gap-1.5 text-muted">
                  {i === 0 ? <span aria-hidden="true" className="h-0.5 w-3 rounded-full bg-brand" /> : null}
                  {r.label}
                </dt>
                <dd className={`tabular-nums ${i === 0 ? "font-semibold text-ink" : "text-ink"}`}>{r.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}
    </div>
  );
}

/* ── Bar list: ranked horizontal bars with the value at the tip ──────── */

export interface BarItem {
  key: string;
  label: string;
  sublabel?: string;
  value: number;
  /** Shown instead of the raw value, e.g. "1,240 · 62%". */
  display: string;
}

export function BarList({
  items,
  selectedKey,
  onSelect,
  empty,
}: {
  items: readonly BarItem[];
  selectedKey?: string | null;
  /** Makes each row a button, e.g. to filter the dashboard to it. */
  onSelect?: (key: string) => void;
  empty: ReactNode;
}) {
  if (items.length === 0) return <div className="px-5 py-8 text-center text-sm text-muted">{empty}</div>;
  const max = Math.max(1, ...items.map((i) => i.value));

  return (
    <ul className="flex flex-col gap-1 px-3 py-3">
      {items.map((item) => {
        const selected = selectedKey === item.key;
        const body = (
          <>
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-sm text-ink">
                <span className={selected ? "font-semibold" : "font-medium"}>{item.label}</span>
                {item.sublabel ? <span className="ml-1.5 text-xs text-muted">{item.sublabel}</span> : null}
              </span>
              <span className="shrink-0 text-sm tabular-nums text-ink">{item.display}</span>
            </span>
            <span aria-hidden="true" className="mt-1.5 block h-2 w-full rounded-full bg-ink/[0.06]">
              <span
                className="block h-full rounded-l-[2px] rounded-r-[4px] bg-brand transition-[width] duration-500"
                style={{ width: `${Math.max(item.value > 0 ? 1.5 : 0, (item.value / max) * 100)}%` }}
              />
            </span>
          </>
        );
        return (
          <li key={item.key}>
            {onSelect ? (
              <button
                aria-pressed={selected}
                className={`w-full rounded-md px-2 py-2 text-left transition-colors ${
                  selected ? "bg-brand-light" : "hover:bg-ink/[0.035]"
                }`}
                onClick={() => onSelect(item.key)}
                type="button"
              >
                {body}
              </button>
            ) : (
              <div className="px-2 py-2">{body}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ── Heatmap: weekday × hour, one-hue sequential ramp ────────────────── */

const LEVELS = 5;

/** Level 0 is "none"; 1-5 step the brand colour from light to full. */
function cellColor(level: number): string {
  if (level === 0) return "color-mix(in srgb, var(--gq-ink) 5%, var(--gq-surface-raised))";
  return `color-mix(in srgb, var(--gq-color-1) ${level * 20}%, var(--gq-surface-raised))`;
}

export function Heatmap({
  grid,
  rowLabels,
  describe,
  fewer,
  more,
}: {
  /** rows × 24 hours */
  grid: readonly (readonly number[])[];
  rowLabels: readonly string[];
  /** Readout text for a cell, e.g. "Tue 14:00–15:00 · 12 scans". */
  describe: (row: number, hour: number, value: number) => string;
  fewer: string;
  more: string;
}) {
  const [hover, setHover] = useState<{ r: number; c: number } | null>(null);
  const max = Math.max(1, ...grid.flat());
  const level = (v: number) => (v === 0 ? 0 : Math.max(1, Math.ceil((v / max) * LEVELS)));

  // The readout rests on the busiest slot, so the chart says its headline
  // before anyone hovers.
  let peak = { r: 0, c: 0, v: -1 };
  grid.forEach((row, r) =>
    row.forEach((v, c) => {
      if (v > peak.v) peak = { r, c, v };
    }),
  );
  const shown = hover ?? (peak.v > 0 ? { r: peak.r, c: peak.c } : null);

  return (
    <div className="px-5 pt-4 pb-4">
      <div className="overflow-x-auto overflow-y-hidden">
        <div
          className="grid min-w-[34rem] gap-[2px]"
          onPointerLeave={() => setHover(null)}
          role="presentation"
          style={{ gridTemplateColumns: "2.75rem repeat(24, minmax(0, 1fr))" }}
        >
          <span />
          {Array.from({ length: 24 }, (_, hour) => (
            <span className="pb-1 text-center text-[10px] text-muted tabular-nums" key={hour}>
              {hour % 3 === 0 ? String(hour).padStart(2, "0") : ""}
            </span>
          ))}
          {grid.map((row, r) => (
            <div className="contents" key={rowLabels[r]}>
              <span className="flex items-center pr-2 text-[11px] text-muted">{rowLabels[r]}</span>
              {row.map((v, c) => {
                const isShown = shown?.r === r && shown.c === c;
                return (
                  <span
                    aria-hidden="true"
                    className={`h-6 rounded-[3px] transition-shadow ${isShown ? "ring-2 ring-ink/60" : ""}`}
                    key={c}
                    onPointerEnter={() => setHover({ r, c })}
                    style={{ background: cellColor(level(v)) }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p aria-live="polite" className="text-sm text-ink tabular-nums">
          {shown ? describe(shown.r, shown.c, grid[shown.r]?.[shown.c] ?? 0) : " "}
        </p>
        <div aria-hidden="true" className="flex items-center gap-1.5 text-[11px] text-muted">
          {fewer}
          {Array.from({ length: LEVELS + 1 }, (_, l) => (
            <span className="size-3 rounded-[2px]" key={l} style={{ background: cellColor(l) }} />
          ))}
          {more}
        </div>
      </div>
    </div>
  );
}
